import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Search, ChevronRight, Settings } from 'lucide-react';
import WooCommerceIntegration from './WooCommerceIntegration';
import WhatsAppIntegration from './WhatsAppIntegration';
import EmailIntegration from '../../Integrations/EmailIntegration';

/**
 * Integration component for the chatbot builder
 * This component is a container for all integrations with a multi-panel layout
 */
function Integration({
  isEditMode,
  updateConfig,
  saveConfig,
  chatbotId,
  // WooCommerce props
  wooCommerceApiKey,
  setWooCommerceApiKey,
  wooCommerceApiSecret,
  setWooCommerceApiSecret,
  wooCommerceStoreUrl,
  setWooCommerceStoreUrl,
  wooCommerceEnabled,
  setWooCommerceEnabled,
  orderTrackingEnabled,
  setOrderTrackingEnabled,
  // WhatsApp props
  whatsAppEnabled,
  setWhatsAppEnabled,
  whatsAppPhoneNumber,
  setWhatsAppPhoneNumber,
  whatsAppPhoneNumberId,
  setWhatsAppPhoneNumberId,
  whatsAppBusinessAccountId,
  setWhatsAppBusinessAccountId,
  whatsAppDisplayName,
  setWhatsAppDisplayName,
  whatsAppAccessToken,
  setWhatsAppAccessToken,
  // Email props
  emailIntegrations,
  setEmailIntegrations
}) {
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // persistAndUpdate updates the parent config with the new values
  const persistAndUpdate = useCallback((partialConfig) => {
    // 1. Update local state
    updateConfig(partialConfig);

    // 2. Save to backend immediately
    if (saveConfig) {
      saveConfig(partialConfig);
    }
  }, [updateConfig, saveConfig]);

  // Define available integrations
  const integrations = [
    {
      id: 'whatsapp',
      name: 'WhatsApp Business',
      description: 'Connect your WhatsApp Business account to automate responses.',
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#25D366">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      ),
      connected: whatsAppEnabled && !!whatsAppPhoneNumber,
      component: (
        <WhatsAppIntegration
          whatsAppEnabled={whatsAppEnabled}
          setWhatsAppEnabled={setWhatsAppEnabled}
          whatsAppPhoneNumber={whatsAppPhoneNumber}
          setWhatsAppPhoneNumber={setWhatsAppPhoneNumber}
          whatsAppPhoneNumberId={whatsAppPhoneNumberId}
          setWhatsAppPhoneNumberId={setWhatsAppPhoneNumberId}
          whatsAppBusinessAccountId={whatsAppBusinessAccountId}
          setWhatsAppBusinessAccountId={setWhatsAppBusinessAccountId}
          whatsAppDisplayName={whatsAppDisplayName}
          setWhatsAppDisplayName={setWhatsAppDisplayName}
          whatsAppAccessToken={whatsAppAccessToken}
          setWhatsAppAccessToken={setWhatsAppAccessToken}
          persistAndUpdate={persistAndUpdate}
          chatbotId={chatbotId || localStorage.getItem('chatbotId')}
        />
      )
    },
    {
      id: 'woocommerce',
      name: 'WooCommerce',
      description: 'Sync your products and enable order tracking for your store.',
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#96588a">
          <path d="M21.6 15.6l-1.8-8.4c-.3-1.2-1.2-1.8-2.4-1.8H6.6c-1.2 0-2.4.6-2.4 1.8L2.4 15.6c-.6 1.8.6 3 2.4 3h14.4c1.8 0 3-1.2 2.4-3zM12 15c-1.2 0-2.4-.6-2.4-1.8s.6-2.4 1.8-3 1.8.6 1.8 1.8-1.2 3-1.2 3zm6 0c-1.2 0-2.4-.6-2.4-1.8s.6-2.4 1.8-3 1.8.6 1.8 1.8-1.2 3-1.2 3z" />
        </svg>
      ),
      connected: wooCommerceEnabled && !!wooCommerceStoreUrl,
      component: (
        <WooCommerceIntegration
          wooCommerceApiKey={wooCommerceApiKey}
          setWooCommerceApiKey={setWooCommerceApiKey}
          wooCommerceApiSecret={wooCommerceApiSecret}
          setWooCommerceApiSecret={setWooCommerceApiSecret}
          wooCommerceStoreUrl={wooCommerceStoreUrl}
          setWooCommerceStoreUrl={setWooCommerceStoreUrl}
          wooCommerceEnabled={wooCommerceEnabled}
          setWooCommerceEnabled={setWooCommerceEnabled}
          orderTrackingEnabled={orderTrackingEnabled}
          setOrderTrackingEnabled={setOrderTrackingEnabled}
          persistAndUpdate={persistAndUpdate}
        />
      )
    },
    {
      id: 'email',
      name: 'Email (SMTP)',
      description: 'Configure outgoing email settings for notifications.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-blue-500">
          <rect width="20" height="16" x="2" y="4" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
      ),
      connected: emailIntegrations && emailIntegrations.length > 0 && emailIntegrations[0].host,
      component: (
        <EmailIntegration
          chatbotId={chatbotId}
          emailIntegrations={emailIntegrations}
          setEmailIntegrations={setEmailIntegrations}
          persistAndUpdate={persistAndUpdate}
        />
      )
    }
  ];

  // Filter integrations based on search query
  const filteredIntegrations = integrations.filter(integration =>
    integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    integration.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedIntegration = integrations.find(i => i.id === selectedIntegrationId);

  return (
    <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
      {/* Left Panel - Integration List */}
      <div className="w-[320px] h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden flex-shrink-0">
        <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10">
          <h2 className="font-bold text-lg text-slate-800 mb-1">Integrations</h2>
          <p className="text-xs text-slate-500 mb-4">Connect external services to your chatbot.</p>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search integrations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium text-slate-700 placeholder:text-slate-400"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
          {filteredIntegrations.length > 0 ? (
            <div className="space-y-1">
              {filteredIntegrations.map((integration) => (
                <button
                  key={integration.id}
                  onClick={() => setSelectedIntegrationId(integration.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-200 text-left group
                    ${selectedIntegrationId === integration.id
                      ? 'bg-blue-50/80 border border-blue-100 shadow-sm'
                      : 'hover:bg-slate-50 border border-transparent hover:border-slate-100'
                    }`}
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    {React.cloneElement(integration.icon, { className: 'w-9 h-9' })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className={`text-sm font-semibold truncate ${selectedIntegrationId === integration.id ? 'text-slate-900' : 'text-slate-700'}`}>
                        {integration.name}
                      </span>
                      {integration.connected && (
                        <div className="w-2 h-2 rounded-full bg-green-500" title="Connected" />
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 line-clamp-1 leading-relaxed">
                      {integration.description}
                    </p>
                  </div>
                  {selectedIntegrationId === integration.id && (
                    <ChevronRight className="w-4 h-4 text-blue-500 opacity-100" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-40 text-center px-4">
              <p className="text-sm font-medium text-slate-500">No integrations found</p>
              <p className="text-xs text-slate-400 mt-1">Try searching for something else</p>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Configuration Details */}
      <div className="flex-1 h-full bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col relative min-w-[400px]">
        {selectedIntegration ? (
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="px-8 py-6 border-b border-slate-100 bg-white sticky top-0 z-10 flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                {React.cloneElement(selectedIntegration.icon, { className: 'w-10 h-10' })}
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">{selectedIntegration.name}</h2>
                <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                  <span>Configuration & Settings</span>
                  {selectedIntegration.connected ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium border border-green-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium border border-slate-200">
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-8">
              <div className="max-w-2xl mx-auto">
                {selectedIntegration.component}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 bg-slate-50/30">
            <div className="w-20 h-20 bg-slate-100 rounded-3xl flex items-center justify-center mb-6 shadow-sm border border-slate-100 text-slate-300">
              <Settings size={40} />
            </div>
            <h3 className="text-lg font-bold text-slate-700 mb-2">Configure Integrations</h3>
            <p className="text-sm text-slate-500 max-w-xs text-center leading-relaxed">
              Select an integration from the list on the left to configure settings and connection details.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

Integration.propTypes = {
  isEditMode: PropTypes.bool,
  updateConfig: PropTypes.func.isRequired,
  saveConfig: PropTypes.func,
  chatbotId: PropTypes.string,
  // WooCommerce props
  wooCommerceApiKey: PropTypes.string.isRequired,
  setWooCommerceApiKey: PropTypes.func.isRequired,
  wooCommerceApiSecret: PropTypes.string.isRequired,
  setWooCommerceApiSecret: PropTypes.func.isRequired,
  wooCommerceStoreUrl: PropTypes.string.isRequired,
  setWooCommerceStoreUrl: PropTypes.func.isRequired,
  wooCommerceEnabled: PropTypes.bool.isRequired,
  setWooCommerceEnabled: PropTypes.func.isRequired,
  orderTrackingEnabled: PropTypes.bool.isRequired,
  setOrderTrackingEnabled: PropTypes.func.isRequired,
  // WhatsApp props
  whatsAppEnabled: PropTypes.bool,
  setWhatsAppEnabled: PropTypes.func,
  whatsAppPhoneNumber: PropTypes.string,
  setWhatsAppPhoneNumber: PropTypes.func,
  whatsAppPhoneNumberId: PropTypes.string,
  setWhatsAppPhoneNumberId: PropTypes.func,
  whatsAppBusinessAccountId: PropTypes.string,
  setWhatsAppBusinessAccountId: PropTypes.func,
  whatsAppDisplayName: PropTypes.string,
  setWhatsAppDisplayName: PropTypes.func,
  whatsAppAccessToken: PropTypes.string,
  setWhatsAppAccessToken: PropTypes.func,
  // Email props
  emailIntegrations: PropTypes.array,
  setEmailIntegrations: PropTypes.func,
};

Integration.defaultProps = {
  whatsAppEnabled: false,
  whatsAppPhoneNumber: '',
  whatsAppPhoneNumberId: '',
  whatsAppBusinessAccountId: '',
  whatsAppDisplayName: '',
  whatsAppAccessToken: '',
  emailIntegrations: [],
  setEmailIntegrations: () => { },
};

export default Integration;
